import * as THREE from 'three';
// packages/character-render/src/humanoidRig.ts
//
// Pure rig model behind <HumanoidMesh>. Turns a list of voxel parts into
// batched instances (one batch per surface material) with a rest matrix and
// a bone per instance, plus the bone pivots the idle/walk poses rotate
// around. No React, no per-frame allocation: the animator reuses the
// matrices built here.
export interface CharacterVoxel {
  x: number;
  y: number;
  z: number;
  w?: number;
  h?: number;
  d?: number;
  color: string;
}

export interface CharacterPart {
  name:
    | 'head'
    | 'torso'
    | 'body'
    | 'left_arm'
    | 'right_arm'
    | 'left_leg'
    | 'right_leg'
    | 'front_left_leg'
    | 'front_right_leg'
    | 'back_left_leg'
    | 'back_right_leg'
    | 'misc'
    | 'left_wing'
    | 'right_wing'
    | 'tail'
    | 'left_upper_arm'
    | 'left_lower_arm'
    | 'left_hand'
    | 'right_upper_arm'
    | 'right_lower_arm'
    | 'right_hand'
    | 'left_upper_leg'
    | 'left_lower_leg'
    | 'left_foot'
    | 'right_upper_leg'
    | 'right_lower_leg'
    | 'right_foot'
    | 'front_left_upper_leg'
    | 'front_left_lower_leg'
    | 'front_left_foot'
    | 'front_right_upper_leg'
    | 'front_right_lower_leg'
    | 'front_right_foot'
    | 'back_left_upper_leg'
    | 'back_left_lower_leg'
    | 'back_left_foot'
    | 'back_right_upper_leg'
    | 'back_right_lower_leg'
    | 'back_right_foot'
    | 'mid_left_upper_leg'
    | 'mid_left_lower_leg'
    | 'mid_left_foot'
    | 'mid_right_upper_leg'
    | 'mid_right_lower_leg'
    | 'mid_right_foot'
    | 'rear_left_upper_leg'
    | 'rear_left_lower_leg'
    | 'rear_left_foot'
    | 'rear_right_upper_leg'
    | 'rear_right_lower_leg'
    | 'rear_right_foot'
    | 'neck'
    | 'left_horn'
    | 'right_horn'
    | 'spine_1'
    | 'spine_2'
    | 'spine_3'
    | 'spine_4'
    | 'spine_5'
    | 'snake_segment';
  voxels: CharacterVoxel[];
  scale?: [number, number, number];
  offset?: [number, number, number];
  rotation?: [number, number, number];
  bone?: string;
  roughness?: number;
  metalness?: number;
}

/** Rigid bones a humanoid part can ride on. Head and arms hang off the body. */
export type HumanoidBone =
  | 'root'
  | 'body'
  | 'head'
  | 'left_arm'
  | 'right_arm'
  | 'left_leg'
  | 'right_leg';

export const HUMANOID_BONES: readonly HumanoidBone[] = [
  'root',
  'body',
  'head',
  'left_arm',
  'right_arm',
  'left_leg',
  'right_leg',
];

/** Optional surface + rig hints a part may carry on top of `CharacterPart`. */
export interface HumanoidPartStyle {
  /** Bone the part rides on. Default: derived from the part name. */
  bone?: HumanoidBone;
  /** Surface roughness 0..1. Default: per bone (skin 0.6, cloth 0.85, boots 0.9). */
  roughness?: number;
  /** Surface metalness 0..1. Default 0. */
  metalness?: number;
  /** Emissive colour (e.g. a lit lantern or glowing eyes). Default none. */
  emissive?: string;
}

export type HumanoidPart = CharacterPart & HumanoidPartStyle;

const BONE_INDEX: Record<HumanoidBone, number> = {
  root: 0,
  body: 1,
  head: 2,
  left_arm: 3,
  right_arm: 4,
  left_leg: 5,
  right_leg: 6,
};

/** Map a part name onto a bone. Quadruped/creature limbs stay on the root. */
export function boneForPart(name: string): HumanoidBone {
  if (name === 'head' || name === 'neck' || name === 'left_horn' || name === 'right_horn')
    return 'head';
  if (
    name === 'left_arm' ||
    name === 'left_upper_arm' ||
    name === 'left_lower_arm' ||
    name === 'left_hand'
  )
    return 'left_arm';
  if (
    name === 'right_arm' ||
    name === 'right_upper_arm' ||
    name === 'right_lower_arm' ||
    name === 'right_hand'
  )
    return 'right_arm';
  if (
    name === 'left_leg' ||
    name === 'left_upper_leg' ||
    name === 'left_lower_leg' ||
    name === 'left_foot'
  )
    return 'left_leg';
  if (
    name === 'right_leg' ||
    name === 'right_upper_leg' ||
    name === 'right_lower_leg' ||
    name === 'right_foot'
  )
    return 'right_leg';
  if (
    name.includes('leg') ||
    name.includes('foot') ||
    name === 'tail' ||
    name.startsWith('spine') ||
    name === 'snake_segment'
  ) {
    return 'root';
  }
  return 'body';
}

const DEFAULT_ROUGHNESS: Record<HumanoidBone, number> = {
  root: 0.85,
  body: 0.85,
  head: 0.6,
  left_arm: 0.8,
  right_arm: 0.8,
  left_leg: 0.9,
  right_leg: 0.9,
};

/** Surface a batch of instances shares. Colours ride on instanceColor. */
export interface SurfaceKey {
  roughness: number;
  metalness: number;
  emissive: string | null;
}

export interface RigBatch {
  /** Stable id: surface + count, so React remounts the InstancedMesh when the count changes. */
  key: string;
  surface: SurfaceKey;
  count: number;
  /** Rest-pose matrix per instance (part transform × voxel translation). */
  rest: THREE.Matrix4[];
  /** Bone index per instance (see HUMANOID_BONES). */
  bones: Uint8Array;
  /** Linear-space colour per instance. */
  colors: THREE.Color[];
}

export interface HumanoidRig {
  batches: RigBatch[];
  /** Pivot per bone in figure space (HUMANOID_BONES order). */
  pivots: THREE.Vector3[];
  /** Figure height (max y − min y); the poses scale their bob by it. */
  height: number;
  /** Lowest point of the figure (the feet). */
  minY: number;
  instanceCount: number;
}

function quantize(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Roughness buckets. Snapping keeps a figure to a handful of materials (and
 * draw calls) — the eye can't tell 0.85 cloth from 0.9 cloth, but it can tell
 * glossy from skin from matte.
 */
export const ROUGHNESS_BUCKETS = [0.35, 0.6, 0.85] as const;

export function snapRoughness(r: number): number {
  let best: number = ROUGHNESS_BUCKETS[0];
  for (const b of ROUGHNESS_BUCKETS) if (Math.abs(b - r) < Math.abs(best - r)) best = b;
  return best;
}

// Scratch objects are created lazily: many app tests partially mock `three`,
// so nothing from it may be touched at module scope.
let buildScratch: {
  scale: THREE.Vector3;
  pos: THREE.Vector3;
  quat: THREE.Quaternion;
  euler: THREE.Euler;
  voxel: THREE.Matrix4;
} | null = null;
function getBuildScratch() {
  buildScratch ??= {
    scale: new THREE.Vector3(),
    pos: new THREE.Vector3(),
    quat: new THREE.Quaternion(),
    euler: new THREE.Euler(),
    voxel: new THREE.Matrix4(),
  };
  return buildScratch;
}

/**
 * Build the batched rig. The part transform is offset × rotation × scale,
 * and the scale applies to the box as well as to the voxel positions, so a
 * single-voxel part with `scale: [0.5, 1, 0.5]` is a 0.5×1×0.5 box (earlier
 * versions scaled only the position, which left full-size cubes overlapping
 * and z-fighting).
 */
export function buildHumanoidRig(parts: readonly HumanoidPart[]): HumanoidRig {
  const { scale: _scale, pos: _pos, quat: _quat, euler: _euler, voxel: _voxel } = getBuildScratch();
  const byKey = new Map<
    string,
    { surface: SurfaceKey; rest: THREE.Matrix4[]; bones: number[]; colors: THREE.Color[] }
  >();
  const boneMin: THREE.Vector3[] = HUMANOID_BONES.map(
    () =>
      new THREE.Vector3(
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
      ),
  );
  const boneMax: THREE.Vector3[] = HUMANOID_BONES.map(
    () =>
      new THREE.Vector3(
        Number.NEGATIVE_INFINITY,
        Number.NEGATIVE_INFINITY,
        Number.NEGATIVE_INFINITY,
      ),
  );
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let instanceCount = 0;

  for (const part of parts) {
    const bone = part.bone ?? boneForPart(part.name);
    const boneIdx = BONE_INDEX[bone];
    const sx = part.scale?.[0] ?? 1;
    const sy = part.scale?.[1] ?? 1;
    const sz = part.scale?.[2] ?? 1;
    _scale.set(sx, sy, sz);
    _pos.set(part.offset?.[0] ?? 0, part.offset?.[1] ?? 0, part.offset?.[2] ?? 0);
    const r = part.rotation;
    _euler.set(r?.[0] ?? 0, r?.[1] ?? 0, r?.[2] ?? 0);
    _quat.setFromEuler(_euler);
    const partMatrix = new THREE.Matrix4().compose(_pos, _quat, _scale);

    const surface: SurfaceKey = {
      roughness: snapRoughness(part.roughness ?? DEFAULT_ROUGHNESS[bone]),
      metalness: quantize(part.metalness ?? 0),
      emissive: part.emissive ?? null,
    };
    const key = `${surface.roughness}|${surface.metalness}|${surface.emissive ?? '-'}`;
    let batch = byKey.get(key);
    if (!batch) {
      batch = { surface, rest: [], bones: [], colors: [] };
      byKey.set(key, batch);
    }

    // Half-extent of one voxel box after the part scale (rotation ignored for
    // the bounds; parts that rotate are rare and the pivot is approximate).
    const hx = Math.abs(sx) / 2;
    const hy = Math.abs(sy) / 2;
    const hz = Math.abs(sz) / 2;

    for (const v of part.voxels) {
      const m = new THREE.Matrix4().multiplyMatrices(
        partMatrix,
        _voxel.makeTranslation(v.x, v.y, v.z),
      );
      batch.rest.push(m);
      batch.bones.push(boneIdx);
      batch.colors.push(new THREE.Color(v.color));
      instanceCount++;

      _pos.setFromMatrixPosition(m);
      boneMin[boneIdx]!.min(_pos.clone().sub(new THREE.Vector3(hx, hy, hz)));
      boneMax[boneIdx]!.max(_pos.clone().add(new THREE.Vector3(hx, hy, hz)));
      minY = Math.min(minY, _pos.y - hy);
      maxY = Math.max(maxY, _pos.y + hy);
    }
  }

  if (!Number.isFinite(minY)) {
    minY = 0;
    maxY = 0;
  }

  const pivots = HUMANOID_BONES.map((bone, i) => {
    const lo = boneMin[i]!;
    const hi = boneMax[i]!;
    if (!Number.isFinite(lo.x)) return new THREE.Vector3(0, minY, 0);
    const cx = (lo.x + hi.x) / 2;
    const cz = (lo.z + hi.z) / 2;
    switch (bone) {
      case 'head':
        return new THREE.Vector3(cx, lo.y, cz); // neck
      case 'left_arm':
      case 'right_arm':
        return new THREE.Vector3(cx, hi.y - (hi.y - lo.y) * 0.08, cz); // shoulder
      case 'left_leg':
      case 'right_leg':
        return new THREE.Vector3(cx, hi.y, cz); // hip
      case 'body':
        return new THREE.Vector3(cx, lo.y, cz); // waist
      default:
        return new THREE.Vector3(0, minY, 0); // feet
    }
  });

  const batches: RigBatch[] = Array.from(byKey.entries()).map(([key, b]) => ({
    key: `${key}#${b.rest.length}`,
    surface: b.surface,
    count: b.rest.length,
    rest: b.rest,
    bones: Uint8Array.from(b.bones),
    colors: b.colors,
  }));

  return { batches, pivots, height: maxY - minY, minY, instanceCount };
}

// ---------------------------------------------------------------------------
// Poses
// ---------------------------------------------------------------------------

/** Built-in procedural animations. `none` keeps the rest pose (no per-frame work). */
export type HumanoidAnimation = 'none' | 'idle' | 'walk' | 'attack' | 'death';

/** Rotation (radians, XYZ) + vertical lift (fraction of figure height) for one bone. */
export interface BonePose {
  rx: number;
  ry: number;
  rz: number;
  lift: number;
}

export function emptyPose(): BonePose {
  return { rx: 0, ry: 0, rz: 0, lift: 0 };
}

/**
 * Procedural pose for one bone at time `t` (seconds). Pure and allocation
 * free: writes into `out`. `phase` de-synchronises a crowd of NPCs.
 *
 * - idle: breathing bob (body lifts ~1.2% of height), a slow head turn,
 *   arms settle slightly away from the body.
 * - walk: opposite arm/leg swing and a step bob.
 * - attack: right arm chops forward on a 0.6 s loop.
 * - death: the whole figure tips over sideways within 0.6 s and stays down.
 */
export function poseHumanoidBone(
  animation: HumanoidAnimation,
  bone: HumanoidBone,
  t: number,
  phase: number,
  out: BonePose,
): BonePose {
  out.rx = 0;
  out.ry = 0;
  out.rz = 0;
  out.lift = 0;
  if (animation === 'none') return out;

  if (animation === 'idle') {
    const breath = Math.sin(t * 2.1 + phase);
    switch (bone) {
      case 'body':
        out.lift = 0.006 + breath * 0.006;
        out.rx = breath * 0.012;
        break;
      case 'head':
        // Two incommensurate sines: the gaze drifts instead of ticking back and forth.
        out.ry = Math.sin(t * 0.37 + phase * 1.7) * 0.42 + Math.sin(t * 0.91 + phase) * 0.08;
        out.rx = Math.sin(t * 0.53 + phase) * 0.05 - breath * 0.015;
        break;
      case 'left_arm':
        out.rz = -0.05 - breath * 0.02;
        out.rx = Math.sin(t * 0.8 + phase) * 0.04;
        break;
      case 'right_arm':
        out.rz = 0.05 + breath * 0.02;
        out.rx = Math.sin(t * 0.8 + phase + 1.3) * 0.04;
        break;
      default:
        break;
    }
    return out;
  }

  if (animation === 'walk') {
    const s = Math.sin(t * 6 + phase);
    switch (bone) {
      case 'body':
        out.lift = Math.abs(Math.cos(t * 6 + phase)) * 0.015;
        break;
      case 'head':
        out.ry = s * 0.05;
        break;
      case 'left_arm':
        out.rx = -s * 0.5;
        break;
      case 'right_arm':
        out.rx = s * 0.5;
        break;
      case 'left_leg':
        out.rx = s * 0.55;
        break;
      case 'right_leg':
        out.rx = -s * 0.55;
        break;
      default:
        break;
    }
    return out;
  }

  if (animation === 'attack') {
    const k = ((t + phase) % 0.6) / 0.6;
    const chop = Math.sin(k * Math.PI);
    if (bone === 'right_arm') out.rx = -1.6 * chop;
    if (bone === 'body') out.ry = -0.25 * chop;
    return out;
  }

  // death
  if (bone === 'root') out.rz = Math.min(1, t / 0.6) * (Math.PI / 2) * 0.95;
  return out;
}

let poseScratch: {
  boneLocal: THREE.Matrix4;
  t1: THREE.Matrix4;
  t2: THREE.Matrix4;
  q: THREE.Quaternion;
  e: THREE.Euler;
} | null = null;
function getPoseScratch() {
  poseScratch ??= {
    boneLocal: new THREE.Matrix4(),
    t1: new THREE.Matrix4(),
    t2: new THREE.Matrix4(),
    q: new THREE.Quaternion(),
    e: new THREE.Euler(),
  };
  return poseScratch;
}

function pivotRotation(
  out: THREE.Matrix4,
  pivot: THREE.Vector3,
  pose: BonePose,
  liftY: number,
): THREE.Matrix4 {
  const { t1: _t1, t2: _t2, q: _q, e: _e } = getPoseScratch();
  _e.set(pose.rx, pose.ry, pose.rz);
  _q.setFromEuler(_e);
  // T(pivot + lift) · R · T(−pivot)
  _t1.makeRotationFromQuaternion(_q);
  _t2.makeTranslation(-pivot.x, -pivot.y, -pivot.z);
  out.multiplyMatrices(_t1, _t2);
  _t1.makeTranslation(pivot.x, pivot.y + liftY, pivot.z);
  return out.premultiply(_t1);
}

/**
 * Compute every bone's world-in-figure matrix for the current pose. Head and
 * arms inherit the body (so they bob with the breath); legs hang off the root.
 * `out` must hold HUMANOID_BONES.length matrices.
 */
export function computeBoneMatrices(
  rig: Pick<HumanoidRig, 'pivots' | 'height'>,
  animation: HumanoidAnimation,
  t: number,
  phase: number,
  poseScratch: BonePose,
  out: THREE.Matrix4[],
): void {
  const h = rig.height || 1;
  const _boneLocal = getPoseScratch().boneLocal;
  // root
  poseHumanoidBone(animation, 'root', t, phase, poseScratch);
  pivotRotation(out[0]!, rig.pivots[0]!, poseScratch, 0);
  // body = root · local
  poseHumanoidBone(animation, 'body', t, phase, poseScratch);
  pivotRotation(_boneLocal, rig.pivots[1]!, poseScratch, poseScratch.lift * h);
  out[1]!.multiplyMatrices(out[0]!, _boneLocal);
  // head + arms = body · local
  for (let i = 2; i <= 4; i++) {
    poseHumanoidBone(animation, HUMANOID_BONES[i]!, t, phase, poseScratch);
    pivotRotation(_boneLocal, rig.pivots[i]!, poseScratch, poseScratch.lift * h);
    out[i]!.multiplyMatrices(out[1]!, _boneLocal);
  }
  // legs = root · local
  for (let i = 5; i <= 6; i++) {
    poseHumanoidBone(animation, HUMANOID_BONES[i]!, t, phase, poseScratch);
    pivotRotation(_boneLocal, rig.pivots[i]!, poseScratch, poseScratch.lift * h);
    out[i]!.multiplyMatrices(out[0]!, _boneLocal);
  }
}
